import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import useCommonMap from "./useCommonMap";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { Badge } from "react-bootstrap";
import circle from "../assets/img/m1.png/clusterIcon.png";
import defaultImg from "../assets/img/icon/Aircraft/Airliner_Black.png";
import { imageSet } from "../pages/icons/AssetIcon/IconList";
import { getDistance, getUrl } from "../utils/staticMethods";
import useSrlHelper from "./useSrlHelper";

const useNearbyItemsManager = ({
  showNearByAssets,
  showNearByZones,
  nearbyItems,
  onCurrentChanged,
  onClusteredItemsSelected,
}) => {
  const [nearbyTrackedItems, setNearbyTrackedItems] = useState([]);
  const [nearbyZones, setNearbyZones] = useState([]);
  const zonesMarkersRef = useRef(new Map());
  const markersStateRef = useRef({ items: new Map(), groups: new Map() });
  const markerClustererRef = useRef();
  const { itemDisplayName } = useSrlHelper();
  const commonMap = useCommonMap();
  const {
    map,
    mapLibCore,
    mapLibMaps,
    mapLibMarker,
    mapLibGeometry,
    mapLibPlaces,
  } = commonMap;

  useEffect(() => {
    if (!map || !mapLibMarker) return;
    // Once we have the map and the markerLib loaded, create an empty clusterer
    markerClustererRef.current = new MarkerClusterer({
      map: map,
      // markers: markers.values(),
      // markers: markersRef.current.values().toArray(),
      algorithm: new SuperClusterAlgorithm({
        radius: 30,
        maxZoom: 15,
      }),
      renderer: {
        render({ position, count }, stats, map) {
          var marker = new mapLibMarker.AdvancedMarkerElement({
            // map: map,
            position: position,
          });
          //const myDiv = document.createElement("div");
          ReactDOM.render(
            // <div
            //   style={{
            //     // backgroundColor: "blueviolet",
            //     // color: "white",
            //     textAlign: "center",
            //   }}
            // >
            //   <img src={circle} width={"24px"} alt="Cluster" />
            //   <br />
            //   <Badge pill bg="info">
            //     {count}
            //   </Badge>
            // </div>
            <div style={{ position: "relative", transform: "translateY(50%)" }}>
              <img src={circle} width={"24px"} alt="Cluster" />

              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginTop: 4,
                  pointerEvents: "none",
                }}
              >
                <Badge pill bg="info">
                  {count}
                </Badge>
              </div>
            </div>,
            marker.element
          );
          // marker.appendChild(myDiv);
          // marker.style.translate = "0 12px";

          return marker;
        },
      },
      onClusterClick(e, cluster) {
        map.setZoom(Math.min(16, map.getZoom() + 4.0));
        map.panTo(cluster.marker.position);
        // markerClustererRef.current.render();
      },
    });

    // map.setCenter({ lat: 51.26841464875889, lng: -1.088944107925282 });
    // map.setZoom(12);

    // setMarkerClustererReady(true);

    return () => {
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current.map = null;
        //delete markerClustererRef.current;
        markerClustererRef.current = null;
      }
    };
  }, [map, mapLibMarker]);

  useEffect(() => {
    setNearbyTrackedItems(
      nearbyItems
        ? nearbyItems.filter((_) => {
            return showNearByAssets && _.type !== "zone";
          })
        : []
    );
    setNearbyZones(
      nearbyItems
        ? nearbyItems.filter((_) => {
            return showNearByZones && _.type === "zone";
          })
        : []
    );
    // markerClustererRef.current?.render();
  }, [nearbyItems, showNearByAssets, showNearByZones]);

  useEffect(() => {
    if (!nearbyTrackedItems) return;
    if (!mapLibMarker) return;

    if (!markerClustererRef.current) return;

    syncItems(markersStateRef.current, nearbyTrackedItems, {
      createGroupMarker,
      updateGroupMarker,
      destroyGroupMarker: function (group) {
        markerClustererRef.current.removeMarker(group.marker, true);
      },
    });

    markerClustererRef.current.render();
    // Forces redraw
    //map.setOptions({ center: map.getCenter() });
    // setUpdateInfoWindows(true);
  }, [nearbyTrackedItems, mapLibMarker]);

  const createGroupMarker = function (lat, lng, id, group) {
    const item = nearbyTrackedItems.find((_) => _.id === id);

    var marker = new mapLibMarker.AdvancedMarkerElement({
      map,
      position: {
        lat: Number(lat),
        lng: Number(lng),
      },
      title: id.toString(),
    });

    var displayName = itemDisplayName(item);
    const imageSrc = imageSet[getUrl(item.icon)]
      ? require("../assets/img/icon/" + imageSet[getUrl(item.icon)]).default
      : defaultImg;
    ReactDOM.render(
      <div style={{ position: "relative", transform: "translateY(50%)" }}>
        <img src={imageSrc} width={24} alt="Marker" />

        <div
          className="px-1 text-nowrap text-black bg-primary bg-opacity-75 rounded"
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 4,
            pointerEvents: "none",
          }}
        >
          {displayName}
        </div>
      </div>,
      marker.element
    );

    marker.addListener("click", handleMarkerClick.bind(group));
    marker.item = item;

    markerClustererRef.current.addMarker(marker, true);

    return marker;
  };

  const updateGroupMarker = function (group) {
    if (group.ids.length === 1) {
      const item = nearbyTrackedItems.find((_) => _.id === group.ids[0]);
      if (item) {
        var displayName = itemDisplayName(item);
        const imageSrc = imageSet[getUrl(item.icon)]
          ? require("../assets/img/icon/" + imageSet[getUrl(item.icon)]).default
          : defaultImg;
        ReactDOM.render(
          <div style={{ position: "relative", transform: "translateY(50%)" }}>
            <img src={imageSrc} width={24} alt="Marker" />

            <div
              className="px-1 text-nowrap text-black bg-primary bg-opacity-75 rounded"
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginTop: 4,
                pointerEvents: "none",
              }}
            >
              {displayName}
            </div>
          </div>,
          group.marker.element
        );
      }
    } else {
      ReactDOM.render(
        <div style={{ position: "relative", transform: "translateY(50%)" }}>
          <img src={circle} width={"24px"} alt="Cluster" />

          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              marginTop: 4,
              pointerEvents: "none",
            }}
          >
            <Badge pill bg="info">
              {group.ids.length.toString()}
            </Badge>
          </div>
        </div>,
        group.marker.element
      );
    }
  };

  const handleMarkerClick = function (e) {
    console.log(
      "%cClicked %s %s",
      "background: #aaddff",
      JSON.stringify(e),
      JSON.stringify(this.ids)
    );

    if (!this && !this.ids) return;

    if (this.ids.length === 1) {
      const item = nearbyTrackedItems.find((_) => _.id === this.ids[0]);
      if (item) {
        // item.showInfo = true;
        onCurrentChanged({ current: item, marker: this.marker });
        this.showInfo = true;
        this.item = item;
      }
    } else {
      onClusteredItemsSelected && onClusteredItemsSelected(this.ids);
    }
  };

  useEffect(() => {
    syncMap(zonesMarkersRef.current, nearbyZones, {
      onAdd: (item) => createZoneMarker(item), // returns marker instance
      onModify: (item, marker) => updateZoneMarker(marker, item),
      onDelete: (marker) => removeZoneMarker(marker),
    });
  }, [zonesMarkersRef.current, nearbyZones]);

  const createZoneMarker = useCallback((item) => {
    var latitude, longitude;
    try {
      latitude = parseFloat(item.latitude);
      longitude = parseFloat(item.longitude);
    } catch (error) {
      return null;
    }
    if (isNaN(latitude) || isNaN(longitude)) {
      return null;
    }
    var marker = new mapLibMarker.AdvancedMarkerElement({
      map: map,
      position: {
        lat: latitude,
        lng: longitude,
      },
      zIndex: -1,
    });
    const myDiv = document.createElement("div");
    const imageSrc = imageSet[getUrl(item.icon)]
      ? require("../assets/img/icon/" + imageSet[getUrl(item.icon)]).default
      : defaultImg;
    ReactDOM.render(<img src={imageSrc} width={"32px"} alt="Cluster" />, myDiv);
    marker.appendChild(myDiv);
    marker.trackedItem = item;
    marker.style.translate = "0% 50%";

    if (item.radius) {
      let c = new mapLibMaps.Circle({
        id: item.id,
        idsArr: [item.id],
        ids: [item],
        strokeColor: "#090B29",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#090B29",
        fillOpacity: 0.4,
        shape: "circle",
        type: "zone",
        center: {
          lat: Number(item.latitude),
          lng: Number(Number(item.longitude)),
        },
        map: map,
        radius: Number(item.radius),
      });

      marker.geofence = c;
    } else if (item.polygon) {
      // if this zone is a polygon area
      let firstItem = item.polygon[0].split(",");
      let minLat = { lat: Number(firstItem[1]), lng: Number(firstItem[0]) },
        maxLat = { lat: Number(firstItem[1]), lng: Number(firstItem[0]) },
        minLng = { lat: Number(firstItem[1]), lng: Number(firstItem[0]) },
        maxLng = { lat: Number(firstItem[1]), lng: Number(firstItem[0]) };
      let arr = item.polygon.map((item) => {
        let me = item.split(",");
        let lng = Number(me[0]);
        let lat = Number(me[1]);
        if (lat > maxLat.lat) maxLat = { lat, lng };
        if (lat < minLat.lat) minLat = { lat, lng };
        if (lng > maxLng.lng) maxLng = { lat, lng };
        if (lng < minLng.lng) minLng = { lat, lng };
        return { lng, lat };
      });
      let point1 = new mapLibCore.LatLng(minLat);
      let point2 = new mapLibCore.LatLng(maxLat);
      let point3 = new mapLibCore.LatLng(minLng);
      let point4 = new mapLibCore.LatLng(maxLng);
      let d1 = getDistance(point1, point2);
      let d2 = getDistance(point3, point4);
      let d = d1 > d2 ? d1 : d2;
      marker.diam = Number(d);
      let polygon = new mapLibMaps.Polygon({
        paths: arr,
        idsArr: [item.id],
        ids: [item],
        id: item.id,
        type: "zone",
        shape: "polygon",
        strokeColor: "#000000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#242424",
        fillOpacity: 0.4,
        editable: false,
        map: map,
      });

      marker.geofence = polygon;
    }

    return marker;
  });

  const updateZoneMarker = useCallback((marker, item) => {
    if (marker) {
      marker.position = {
        lat: parseFloat(item.latitude),
        lng: parseFloat(item.longitude),
      };
      marker.trackedItem = item;
    }
  });

  const removeZoneMarker = useCallback((marker) => {
    if (marker) {
      if (marker.geofence) {
        marker.geofence.setMap(null);
        marker.geofence = null;
      }
      marker.setMap(null);
    }
  });

  function groupKey(lat, lng) {
    const result = "".concat([
      Number(lat).toFixed(7).toString(),
      Number(lng).toFixed(7).toString(),
    ]);
    return result;
  }

  function syncItems(
    itemsState,
    apiItems,
    { createGroupMarker, updateGroupMarker, destroyGroupMarker }
  ) {
    const seenIds = new Set();

    for (const dto of apiItems) {
      const { id, name, latitude: lat, longitude: lng } = dto;
      const newKey = groupKey(lat, lng);

      seenIds.add(id);

      let item = itemsState.items.get(id);
      let oldKey = null;

      if (!item) {
        item = { id, name, latitude: lat, longitude: lng, groupKey: newKey };
        itemsState.items.set(id, item);
      } else {
        oldKey = item.groupKey;
        item.name = name;
        item.lat = lat;
        item.lng = lng;
        item.groupKey = newKey;
      }

      // Remove from old group if moved
      if (oldKey && oldKey !== newKey) {
        const oldGroup = itemsState.groups.get(oldKey);
        if (oldGroup) {
          // oldGroup.ids.delete(id);
          oldGroup.ids.splice(oldGroup.ids.indexOf(id), 1);

          if (oldGroup.ids.length === 0) {
            // debugger;
            destroyGroupMarker(oldGroup);
            itemsState.groups.delete(oldKey);
          } else {
            updateGroupMarker(oldGroup);
          }
        }
      }

      // Add to new group
      let group = itemsState.groups.get(newKey);
      if (!group) {
        group = { ids: [] };
        group.ids.push(id);
        group.marker = createGroupMarker(lat, lng, id, group);
        itemsState.groups.set(newKey, group);
      } else if (group.ids.indexOf(id) === -1) {
        group.ids.push(id);
        updateGroupMarker(group);
      }
    }

    // debugger;
    // Remove deleted items
    const items = itemsState.items.entries();
    for (const [id, item] of items) {
      if (!seenIds.has(id)) {
        const group = itemsState.groups.get(item.groupKey);
        if (group) {
          group.ids.splice(group.ids.indexOf(id), 1);

          if (group.ids.length === 0) {
            destroyGroupMarker(group);
            itemsState.groups.delete(item.groupKey);
          } else {
            updateGroupMarker(group);
          }
        }
        itemsState.items.delete(id);
      }
    }
  }

  function syncMap(map, items, { onAdd, onModify, onDelete }) {
    const seen = new Set();

    for (const item of items) {
      const existing = map.get(item.id);

      if (!existing) {
        // New item
        const value = onAdd(item);
        // console.log("onAdd");
        map.set(item.id, value);
      } else {
        // Existing item
        const updated = onModify(item, existing);
        // console.log("onModify");
        if (updated !== undefined) {
          map.set(item.id, updated);
        }
      }
      seen.add(item.id);
    }
    // Handle deletions
    for (const [id, value] of map.entries()) {
      if (!seen.has(id)) {
        onDelete(value);
        // console.log("onDelete");
        map.delete(id);
      }
    }
    return map;
  }

  return {
    groups: markersStateRef.current.groups,
    zones: zonesMarkersRef.current,
  };
};

export default useNearbyItemsManager;
